// client/src/components/pages/LexiStar/Dashboard/Dashboard.tsx
//
// LexiStar management dashboard (ST-00188 M4-D, T7-S03; LS-09 §5.15; LS-03
// FR-RPT-001 / FR-RPT-006; m4-contracts §4, §8). Route /lexistar/dashboard,
// page permission lexistar.dashboard.view.
//
//  - Period (presets + the shared date-range picker) and scope (the whole
//    operation, one route or one customer) — both in the URL.
//  - Headline KPI tiles against the previous period (no targets yet — a
//    tenant setting later), each with a sparkline, the provisional / final
//    chip and its provenance; every tile opens the list behind it.
//  - "Top five things to look at" for the last day of the period.
//  - Breakdowns by route and by customer, the metric chosen per card, within
//    the chosen scope (one customer's routes, one route's customers).
//  - Phone: the cards stack (top five first — exception first, LS-09 §1),
//    every drill-through kept (LS-09 §9).
// Every figure comes from the semantic layer (GET /metrics, /kpis,
// /kpis/breakdown, /kpis/top-exceptions); the page formats, it never computes.
//
// ST-00207 (m4b-contracts §5, phone pass): under 640 px or on a touch screen the filters are two
// 44 px fields that open bottom sheets — the period (the shared date sheet, whose quick ranges
// are the preset buttons) and the scope (routes and customers as a list) — and every control is a
// 44 px target. The desktop dashboard is unchanged.

import { type Dispatch, type SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FaChartLine } from 'react-icons/fa';
import { getKpiBreakdown, getKpis, getMetrics, getTopExceptions } from '../../../../services/lexistarAnalytics';
import { NoPermissionPage } from '../StatusBoard/atoms';
import { PageHeader, ThemedSelect, useAuth } from '../../../../types/commonComponents';
import PhoneFilterSheet, { type PhoneChoice } from '../../../common/PhoneFilterSheet';
import { usePhoneLayout } from '../../../common/phoneLayout';
import { PeriodPicker, ProvenanceLegend, SkeletonBlock, cardClasses } from './analyticsAtoms';
import KpiTile from './KpiTile';
import BreakdownCard from './BreakdownCard';
import TopExceptions from './TopExceptions';
import { formatValue } from './metricFormat';
import type {
  BreakdownResponse,
  KpisResponse,
  MetricsCatalogueResponse,
  TopExceptionsResponse,
} from '../../../../types/lexistar-analytics';
import {
  type Period,
  dayLabel,
  grainFor,
  periodLabel,
  previousPeriod,
  readPeriod,
  spanDays,
} from './periodLogic';
import {
  BREAKDOWN_METRICS,
  type BreakdownRow,
  contextLine,
  metricKeptFor,
  type KpiResult,
  type MetricDef,
  type ResolvedTile,
  type TopException,
  normaliseBreakdown,
  normaliseCatalogue,
  normaliseExceptions,
  normaliseKpis,
  readScope,
  resolveTiles,
  scopeParam,
  seriesOf,
  tileMetricKeys,
} from './kpiModel';

type ErrorLike = { response?: { status?: number; data?: { error?: string; message?: string } }; message?: string };
const errorText = (err: ErrorLike, fallback: string): string =>
  err?.response?.status === 403
    ? 'You do not have permission to read these figures.'
    : err?.response?.data?.error || err?.response?.data?.message || err?.message || fallback;

type By = 'route' | 'customer';
interface BreakdownState {
  metric: string | null;
  rows: BreakdownRow[];
  unit: string | null;
  loading: boolean;
  error: string | null;
}
const emptyBreakdown = (metric: string | null = null): BreakdownState => ({ metric, rows: [], unit: null, loading: true, error: null });

/** Scope choices seen in the period so far, first-seen order (a narrowed card lists fewer rows). */
export const mergeChoices = (seen: BreakdownRow[], rows: BreakdownRow[]): BreakdownRow[] => {
  if (!rows.length) return seen;
  const byId = new Map(seen.map((r) => [r.id, r]));
  rows.forEach((r) => byId.set(r.id, r));
  return [...byId.values()];
};

/** KPIs page link carrying the period (and the metric to sort by). */
export const kpisHref = (tab: string, p: Period, metric?: string | null): string => {
  const q = new URLSearchParams({ tab, from: p.from, to: p.to });
  if (metric) q.set('sort', metric);
  return `/lexistar/kpis?${q.toString()}`;
};

export default function Dashboard() {
  const { hasPermission } = useAuth();
  const canView = hasPermission('lexistar.dashboard.view');
  const phone = usePhoneLayout(); // ST-00207
  const [params, setParams] = useSearchParams();
  const period = readPeriod(params);
  const scope = readScope(params);
  const grain = grainFor(period);
  const periodKey = `${period.from}|${period.to}`;
  const scopeKey = scopeParam(scope) ?? 'tenant';

  const update = useCallback(
    (patch: Record<string, string | null>) =>
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          Object.entries(patch).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
          return next;
        },
        { replace: true }
      ),
    [setParams]
  );

  // ── Catalogue → tiles ─────────────────────────────────────────────────────
  const [catalogue, setCatalogue] = useState<Map<string, MetricDef> | null>(null);
  const [catalogueError, setCatalogueError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  useEffect(() => {
    if (!canView) return undefined;
    let alive = true;
    setCatalogueError(null);
    getMetrics()
      .then((res: MetricsCatalogueResponse) => alive && setCatalogue(normaliseCatalogue(res)))
      .catch((err: ErrorLike) => alive && setCatalogueError(errorText(err, 'The metric catalogue could not be loaded.')));
    return () => {
      alive = false;
    };
  }, [canView, reload]);
  const tiles = useMemo(() => (catalogue ? resolveTiles(catalogue) : []), [catalogue]);
  // Within a scope the cards offer only the metrics kept for it (the API refuses the others).
  const breakdownMetrics = useMemo(
    () => (catalogue ? resolveTiles(catalogue, BREAKDOWN_METRICS).filter((t) => metricKeptFor(t.def, scope.type)) : []),
    [catalogue, scope.type]
  );
  const metricKeys = useMemo(() => tileMetricKeys(tiles), [tiles]);

  // ── Headline KPIs ─────────────────────────────────────────────────────────
  const [kpis, setKpis] = useState<KpiResult | null>(null);
  const [kpiLoading, setKpiLoading] = useState(false);
  const [kpiError, setKpiError] = useState<string | null>(null);
  useEffect(() => {
    if (!canView || !metricKeys.length) return undefined;
    let alive = true;
    setKpiLoading(true);
    setKpiError(null);
    getKpis({
      scope_type: scope.type,
      scope_id: scope.id ?? undefined,
      grain,
      from: period.from,
      to: period.to,
      metrics: metricKeys.join(','),
      compare: 'previous',
    })
      .then((res: KpisResponse) => alive && setKpis(normaliseKpis(res)))
      .catch((err: ErrorLike) => {
        if (!alive) return;
        setKpis(null);
        setKpiError(errorText(err, 'The figures could not be loaded.'));
      })
      .finally(() => alive && setKpiLoading(false));
    return () => {
      alive = false;
    };
    // periodKey / scopeKey carry the period and scope.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView, metricKeys, periodKey, scopeKey, grain]);

  // ── Top five ──────────────────────────────────────────────────────────────
  const [exceptions, setExceptions] = useState<TopException[]>([]);
  const [excLoading, setExcLoading] = useState(true);
  const [excError, setExcError] = useState<string | null>(null);
  useEffect(() => {
    if (!canView) return undefined;
    let alive = true;
    setExcLoading(true);
    setExcError(null);
    getTopExceptions({ date: period.to })
      .then((res: TopExceptionsResponse) => alive && setExceptions(normaliseExceptions(res, formatValue)))
      .catch((err: ErrorLike) => {
        if (!alive) return;
        setExceptions([]);
        setExcError(errorText(err, 'The exceptions could not be loaded.'));
      })
      .finally(() => alive && setExcLoading(false));
    return () => {
      alive = false;
    };
  }, [canView, period.to]);

  // ── Breakdowns ────────────────────────────────────────────────────────────
  const firstBreakdown = breakdownMetrics[0]?.key ?? null;
  const [byRoute, setByRoute] = useState<BreakdownState>(emptyBreakdown());
  const [byCustomer, setByCustomer] = useState<BreakdownState>(emptyBreakdown());
  const offered = (m: string | null): m is string => m !== null && breakdownMetrics.some((t) => t.key === m);
  const routeMetric = offered(byRoute.metric) ? byRoute.metric : firstBreakdown;
  const customerMetric = offered(byCustomer.metric) ? byCustomer.metric : firstBreakdown;

  const loadBreakdown = useCallback(
    (by: By, metric: string | null, set: Dispatch<SetStateAction<BreakdownState>>) => {
      if (!metric) return () => {};
      let alive = true;
      set((s) => ({ ...s, metric, loading: true, error: null }));
      getKpiBreakdown({
        metric,
        by,
        ...(scope.type !== 'tenant' && scope.id ? { scope_type: scope.type, scope_id: scope.id } : {}),
        from: period.from,
        to: period.to,
      })
        .then((res: BreakdownResponse) => {
          if (!alive) return;
          const b = normaliseBreakdown(res);
          set({ metric, rows: b.rows, unit: b.unit, loading: false, error: null });
        })
        .catch((err: ErrorLike) => {
          if (!alive) return;
          set({ metric, rows: [], unit: null, loading: false, error: errorText(err, 'The breakdown could not be loaded.') });
        });
      return () => {
        alive = false;
      };
    },
    // periodKey / scopeKey carry the period and scope.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [periodKey, scopeKey]
  );
  useEffect(() => (canView ? loadBreakdown('route', routeMetric, setByRoute) : undefined), [canView, loadBreakdown, routeMetric]);
  useEffect(
    () => (canView ? loadBreakdown('customer', customerMetric, setByCustomer) : undefined),
    [canView, loadBreakdown, customerMetric]
  );

  // Scope choices: the routes and customers with activity in the period (the
  // breakdown rows carry the same scope ids the KPI API takes). Kept as a union
  // for the period: a card narrowed to one scope lists fewer rows, and the
  // picker must still offer the others.
  const [scopeChoices, setScopeChoices] = useState<{ route: BreakdownRow[]; customer: BreakdownRow[] }>({ route: [], customer: [] });
  useEffect(() => {
    setScopeChoices((c) => ({
      route: mergeChoices(c.route, byRoute.rows),
      customer: mergeChoices(c.customer, byCustomer.rows),
    }));
  }, [byRoute.rows, byCustomer.rows]);
  useEffect(() => setScopeChoices({ route: [], customer: [] }), [periodKey]);

  // ── Header ────────────────────────────────────────────────────────────────
  const prev = previousPeriod(period);
  const scopeLabel =
    scope.type === 'tenant'
      ? 'Whole operation'
      : `${scope.type === 'route' ? 'Route' : scope.type === 'customer' ? 'Customer' : scope.type} · ${
          scopeChoices[scope.type as By]?.find((r) => r.id === scope.id)?.label ?? scope.id
        }`;

  const scopeValue = scopeParam(scope) ?? 'tenant';
  const scopeKnown =
    scope.type === 'tenant' || (scopeChoices[scope.type as By] || []).some((r) => r.id === scope.id);
  const scopeOptions: PhoneChoice[] = [
    { value: 'tenant', label: 'Whole operation' },
    ...(!scopeKnown ? [{ value: scopeValue, label: scopeLabel }] : []),
    ...scopeChoices.route.map((r) => ({ value: `route:${r.id}`, label: r.label, section: 'Routes' })),
    ...scopeChoices.customer.map((r) => ({ value: `customer:${r.id}`, label: r.label, section: 'Customers' })),
  ];
  const phoneToolbar = canView ? (
    <>
      <PeriodPicker phone period={period} onChange={(p) => update({ from: p.from, to: p.to })} />
      <PhoneFilterSheet
        title="Choose the scope"
        label="Scope"
        value={scopeLabel}
        groups={[{ id: 'scope', legend: 'Show the figures for', value: scopeValue, choices: scopeOptions }]}
        onApply={(v) => update({ scope: !v.scope || v.scope === 'tenant' ? null : v.scope })}
      />
    </>
  ) : null;

  const toolbar = phone ? (
    phoneToolbar
  ) : canView ? (
    <>
      <PeriodPicker period={period} onChange={(p) => update({ from: p.from, to: p.to })} />
      <div className="w-full sm:w-64">
        <ThemedSelect
          aria-label="Scope"
          value={scopeValue}
          onChange={(e) => update({ scope: e.target.value === 'tenant' ? null : e.target.value })}
          className="text-sm"
        >
          <option value="tenant">Whole operation</option>
          {!scopeKnown && <option value={scopeValue}>{scopeLabel}</option>}
          {scopeChoices.route.length > 0 && (
            <optgroup label="Routes">
              {scopeChoices.route.map((r) => (
                <option key={`r-${r.id}`} value={`route:${r.id}`}>
                  {r.label}
                </option>
              ))}
            </optgroup>
          )}
          {scopeChoices.customer.length > 0 && (
            <optgroup label="Customers">
              {scopeChoices.customer.map((r) => (
                <option key={`c-${r.id}`} value={`customer:${r.id}`}>
                  {r.label}
                </option>
              ))}
            </optgroup>
          )}
        </ThemedSelect>
      </div>
    </>
  ) : null;

  const header = (
    <PageHeader
      icon={FaChartLine}
      title="Management dashboard"
      subtitle="The operation's headline figures, what changed and what to look at."
      toolbar={toolbar}
      inline={false}
    />
  );

  if (!canView) {
    return <NoPermissionPage header={header} message="You do not have permission to view the management dashboard." />;
  }

  const tileDrill = (t: ResolvedTile): string =>
    kpis?.totals[t.key]?.drill || kpisHref(scope.type === 'customer' ? 'route' : 'lane', period, t.key);

  const days = spanDays(period);
  return (
    <div className="min-h-screen bg-slate-50">
      {header}
      <div className={phone ? 'pt-4 pb-20 space-y-4' : 'max-w-[1800px] mx-auto px-3 sm:px-6 py-5 space-y-4'}>
        <p
          className={`${phone ? 'px-3 ' : ''}text-xs text-slate-600 tabular-nums flex flex-wrap gap-x-3 gap-y-1`}
          role="status"
          aria-live="polite"
        >
          <span>
            <span className="font-medium text-slate-800">{scopeLabel}</span> · {periodLabel(period)}
          </span>
          <span>
            compared with {periodLabel(prev)} ({days} day{days === 1 ? '' : 's'})
          </span>
          {kpis && kpis.provisionalPeriods > 0 && (
            <span>
              {kpis.provisionalPeriods} of {kpis.series.length} {grain === 'day' ? 'days' : grain === 'week' ? 'weeks' : 'months'} still
              provisional
            </span>
          )}
          {kpis?.definitions_version && <span>definitions v{kpis.definitions_version}</span>}
        </p>

        {catalogueError && (
          <div role="alert" className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex flex-wrap items-center justify-between gap-2">
            <p className="font-medium text-sm">{catalogueError}</p>
            <button
              type="button"
              onClick={() => setReload((n) => n + 1)}
              className="touch-target px-3 py-1.5 text-xs font-semibold rounded-md border border-red-300 bg-surface text-red-700 hover:bg-red-50"
            >
              Try again
            </button>
          </div>
        )}
        {kpiError && (
          <div role="alert" className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm font-medium">
            {kpiError}
          </div>
        )}

        <div className="grid grid-cols-1 xl:grid-cols-12 gap-4 items-start">
          <section className="xl:col-span-8 order-2 xl:order-1 min-w-0" aria-label="Headline figures">
            {!catalogue && !catalogueError ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-3" aria-busy="true">
                {Array.from({ length: 8 }, (_, i) => (
                  <SkeletonBlock key={i} className="h-40 rounded-xl" />
                ))}
              </div>
            ) : catalogue && !tiles.length ? (
              <div className={`${cardClasses} px-4 py-10 text-center`}>
                <p className="text-sm font-medium text-slate-900">No headline figures to show</p>
                <p className="text-xs text-slate-600 mt-1">
                  None of the dashboard's metrics are in the catalogue you can read. Ask an administrator for the KPI permissions.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-3">
                {tiles.map((t) => {
                  if (kpiLoading && !kpis) return <SkeletonBlock key={t.key} className="h-40 rounded-xl" />;
                  const context = contextLine(t, kpis?.totals, formatValue);
                  return (
                    <KpiTile
                      key={t.key}
                      label={t.spec.label}
                      def={t.def}
                      value={kpis?.totals[t.key]}
                      previous={kpis?.previous[t.key]}
                      better={t.spec.better}
                      trend={kpis ? seriesOf(kpis, t.key) : []}
                      state={kpis?.state ?? null}
                      context={context}
                      drill={tileDrill(t)}
                    />
                  );
                })}
              </div>
            )}
          </section>

          <div className="xl:col-span-4 order-1 xl:order-2 min-w-0">
            <TopExceptions items={exceptions} loading={excLoading} error={excError} dayText={dayLabel(period.to)} />
          </div>

          <div className="xl:col-span-12 order-3 grid grid-cols-1 lg:grid-cols-2 gap-4 min-w-0">
            <BreakdownCard
              id="by-route"
              title="By route"
              metrics={breakdownMetrics}
              metricKey={routeMetric}
              onMetricChange={(m) => setByRoute(emptyBreakdown(m))}
              rows={byRoute.rows}
              unit={byRoute.unit}
              loading={byRoute.loading}
              error={byRoute.error}
              moreHref={kpisHref('route', period, routeMetric)}
              fallbackDrill={(r) => `/lexistar/dashboard?${new URLSearchParams({ from: period.from, to: period.to, scope: `route:${r.id}` })}`}
            />
            <BreakdownCard
              id="by-customer"
              title="By customer"
              metrics={breakdownMetrics}
              metricKey={customerMetric}
              onMetricChange={(m) => setByCustomer(emptyBreakdown(m))}
              rows={byCustomer.rows}
              unit={byCustomer.unit}
              loading={byCustomer.loading}
              error={byCustomer.error}
              moreHref={kpisHref('route', period, customerMetric)}
              fallbackDrill={(r) => `/lexistar/dashboard?${new URLSearchParams({ from: period.from, to: period.to, scope: `customer:${r.id}` })}`}
            />
          </div>
        </div>

        <p className={phone ? 'px-3 text-[11px] text-slate-500' : 'text-[11px] text-slate-500'}>
          Figures come from the governed metric catalogue. <ProvenanceLegend className="mx-1" /> Costs are always estimates.{' '}
          <Link to={kpisHref('lane', period)} className="font-medium text-primary-700 hover:underline">
            Lane, route, truck, driver and run figures
          </Link>
          {' · '}
          <Link to={`/lexistar/dwell?${new URLSearchParams({ from: period.from, to: period.to })}`} className="font-medium text-primary-700 hover:underline">
            Dwell at sites
          </Link>
        </p>
      </div>
    </div>
  );
}

